import { computed, getCurrentInstance, ref, Ref, watch } from "@vue/composition-api";
import {
  InputMassMessage,
  InputMessageAttachment,
  MassMessage,
  MassMessageFile,
} from "@scrile/api-provider/dist/api/MassMessageProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { getElement } from "@/lib/elementHelpers";
import { getNow } from "@/lib/time";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { AttachmentFile } from "@/types";

export default function useMassMessage(massMessage: Ref<MassMessage | undefined>) {
  const vm = getCurrentInstance();
  const { authUserStrict } = useAuthUser();
  const messageEditorRef = ref<HTMLDivElement>();
  const value = ref("");
  const pricePackageId = ref("0");
  const previewFiles = ref<AttachmentFile[]>([]);
  const getNowDate = (): string => getNow().toISOString();
  const scheduledTime = ref(getNowDate());
  const selectedGroupIds = ref<string[]>([]);
  const excludedGroupIds = ref<string[]>([]);

  const allowScheduledMassMessages = computed(() => config.featureScheduledMassMessage);

  const syncFilesWithVault = async () => {
    await vm?.$nextTick();

    if (!messageEditorRef.value) return;
    const element = getElement(messageEditorRef.value);
    const event = new Event("filesUpdated");
    element.dispatchEvent(event);
  };

  const updateAttachmentFiles = (cb: (files: AttachmentFile[]) => void) => cb(previewFiles.value);

  const adapterToPreviewFiles = (files: MassMessageFile[]): AttachmentFile[] => {
    return files.map(f => ({
      uid: f.uuid,
      type: f.metadata.contentType,
      src: getMediaThumbnailUrl(f, f.urlPart),
      publicAccess: f.publicAccess,
    }));
  };
  const adaptAttachmentFilesToSave = (attachmentFiles: AttachmentFile[]): InputMessageAttachment[] => {
    return attachmentFiles.map(f => ({
      uuid: f.uid,
      publicAccess: f.publicAccess,
    }));
  };
  const getMassMessageReadyToSave = (): InputMassMessage => {
    return {
      id: massMessage.value?.id ?? undefined,
      text: value.value.trim(),
      senderId: authUserStrict.value.id,
      pricePackageId: pricePackageId.value,
      groupIds: selectedGroupIds.value,
      excludedGroupIds: excludedGroupIds.value,
      files: adaptAttachmentFilesToSave(previewFiles.value),
      publishTime: scheduledTime.value,
    };
  };

  watch(
    massMessage,
    newValue => {
      if (!newValue) return;

      value.value = newValue?.text;
      pricePackageId.value = newValue.pricePackageId;
      previewFiles.value = adapterToPreviewFiles(newValue.files);
      scheduledTime.value = newValue.publishTime;
      selectedGroupIds.value = newValue.groupIds;
      excludedGroupIds.value = newValue.excludedGroupIds;

      syncFilesWithVault();
    },
    { immediate: true }
  );

  return {
    value,
    previewFiles,
    scheduledTime,
    pricePackageId,
    messageEditorRef,
    selectedGroupIds,
    excludedGroupIds,
    allowScheduledMassMessages,
    getNowDate,
    updateAttachmentFiles,
    getMassMessageReadyToSave,
  };
}
